/**
 * Home view-models (sections 25–26) — the pure, client-side selectors behind
 * the Personal Home ("My Vector") and Team Home ("Team Vector"). They fold the
 * already-loaded {@link EntitySnapshot} into lightweight, awareness-oriented
 * view-models: what's mine / in-flight, what changed, what's blocked, what's
 * coming up.
 *
 * STRICT posture (section 28): NOTHING here is a productivity, velocity,
 * commit-count, leaderboard, ranking, or per-person performance measure. "My
 * Vector" organizes an individual's own context for THEM; "Team Vector" gives
 * everyone (managers and engineers alike) the same shared engineering picture.
 * Sprint % is a delivery-progress signal for the team's work, never a per-person
 * output metric.
 *
 * Pure TS — no React.
 */

import type { GoalSummary, GoalStatus } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { BlockerSummary } from '../blockers/types';
import type { TeamDocumentSummary } from '../documents/types';
import type { DecisionSummary } from '../decisions/types';
import type { EngineeringChangeSummary } from '../changes/types';
import type { Task } from '../tasks/types';
import type { EntitySnapshot } from '../intelligence/types';
import {
  describeBlockerImpact,
  selectOverallBlockerImpact,
} from '../blockers/blockerImpact';

/** Case-insensitive alias/name equality (blank-safe). */
function eq(a?: string, b?: string): boolean {
  const x = (a ?? '').trim().toLowerCase();
  const y = (b ?? '').trim().toLowerCase();
  return !!x && x === y;
}

/** Parses a yyyy-mm-dd or full-ISO string to epoch ms, or NaN. */
function parseMs(raw?: string): number {
  const s = (raw ?? '').trim();
  if (!s) return Number.NaN;
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return d.getTime();
}

/** Today's local date as yyyy-mm-dd. */
export function todayLocalDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** True when an ISO/date string falls within the last `days` days of `now`. */
function withinDays(raw: string | undefined, days: number, now: Date): boolean {
  const t = parseMs(raw);
  if (Number.isNaN(t)) return false;
  return t >= now.getTime() - days * MS_PER_DAY && t <= now.getTime() + MS_PER_DAY;
}

/** Sorts a list by a date-ish accessor, newest first. */
function byRecency<T>(list: T[], iso: (x: T) => string | undefined): T[] {
  return list
    .slice()
    .sort((a, b) => (parseMs(iso(b)) || 0) - (parseMs(iso(a)) || 0));
}

/** Whether a workstream stage reads as "done/complete" (soft, case-insensitive). */
function isCompleteStage(stage: string): boolean {
  const s = stage.trim().toLowerCase();
  return s === 'complete' || s === 'completed' || s === 'done';
}

// ---------------------------------------------------------------------------
// Upcoming milestones (needs hydrated goals — nextMilestone lives in the body)
// ---------------------------------------------------------------------------

/** A resolved, dated goal milestone for the homes' "upcoming" sections. */
export interface MilestoneItem {
  goalId: string;
  goalTitle: string;
  label: string;
  dueDate: string;
  /** Whole days until due (negative = overdue). */
  daysUntil: number;
  overdue: boolean;
}

/** Minimal goal shape the milestone collector needs (from a hydrated Goal). */
export interface GoalWithMilestone {
  id: string;
  title: string;
  status: GoalStatus;
  nextMilestone?: { label: string; dueDate?: string };
}

/**
 * Collects dated milestones from hydrated goals, keeping those due within the
 * look-ahead window OR recently overdue (still-open goals only), sorted by due
 * date. Completed goals' milestones are dropped (nothing to look forward to).
 */
export function collectUpcomingMilestones(
  goals: GoalWithMilestone[],
  now: Date = new Date(),
  lookAheadDays = 60,
  overdueGraceDays = 30,
): MilestoneItem[] {
  const items: MilestoneItem[] = [];
  for (const g of goals) {
    const due = g.nextMilestone?.dueDate;
    const label = g.nextMilestone?.label;
    if (!due || !label || g.status === 'COMPLETED') continue;
    const t = parseMs(due);
    if (Number.isNaN(t)) continue;
    const daysUntil = Math.round((t - now.getTime()) / MS_PER_DAY);
    if (daysUntil > lookAheadDays) continue;
    if (daysUntil < -overdueGraceDays) continue;
    items.push({
      goalId: g.id,
      goalTitle: g.title,
      label,
      dueDate: due,
      daysUntil,
      overdue: daysUntil < 0,
    });
  }
  return items.sort((a, b) => parseMs(a.dueDate) - parseMs(b.dueDate));
}

// ---------------------------------------------------------------------------
// My Vector (personal home)
// ---------------------------------------------------------------------------

/** The personal-home view-model for one user. Organization/awareness only. */
export interface MyVectorModel {
  alias: string;
  /** My in-progress stories (assigned to me, not Done). */
  activeStories: Task[];
  /** Workstreams I own, am POC for, or follow. */
  workstreams: WorkstreamSummary[];
  /** Goals I lead or contribute to (via my goals / my workstreams). */
  goals: GoalSummary[];
  /** My open blockers (things blocking me). */
  openBlockers: BlockerSummary[];
  /** Documents in review that list me as a reviewer. */
  docsAwaitingReview: TeamDocumentSummary[];
  /** Recent changes in things I follow (my/followed workstreams, my goals, me). */
  recentFollowedChanges: EngineeringChangeSummary[];
  /** Whether I've posted a daily update for today. */
  dailyUpdateSubmittedToday: boolean;
  /** Today's local date (yyyy-mm-dd). */
  today: string;
}

/**
 * Builds {@link MyVectorModel} for `alias` from a snapshot. Everything is a
 * simple ownership/relationship filter over what the team has recorded — no
 * scoring, no counts-as-judgement.
 */
export function buildMyVector(
  snapshot: EntitySnapshot,
  alias: string,
  now: Date = new Date(),
): MyVectorModel {
  const today = todayLocalDate(now);

  const activeStories = snapshot.tasks.filter(
    (t) => !t.deleted && eq(t.assignee, alias) && t.status !== 'DONE',
  );

  const workstreams = snapshot.workstreams.filter(
    (w) =>
      eq(w.engineer, alias) ||
      eq(w.poc, alias) ||
      (w.followers ?? []).some((f) => eq(f, alias)),
  );
  const myWorkstreamIds = new Set(workstreams.map((w) => w.id));
  const myWorkstreamGoalIds = new Set(
    workstreams.map((w) => w.goalId).filter((id): id is string => !!id),
  );

  const goals = snapshot.goals.filter(
    (g) => eq(g.goalLead, alias) || myWorkstreamGoalIds.has(g.id),
  );
  const myGoalIds = new Set(goals.map((g) => g.id));

  const openBlockers = snapshot.blockers.filter(
    (b) => b.status === 'OPEN' && eq(b.blockedEngineer, alias),
  );

  const docsAwaitingReview = snapshot.documents.filter(
    (d) =>
      d.status === 'IN_REVIEW' && (d.reviewers ?? []).some((r) => eq(r, alias)),
  );

  const recentFollowedChanges = byRecency(
    snapshot.changes.filter(
      (c) =>
        eq(c.engineer, alias) ||
        (c.workstreamId && myWorkstreamIds.has(c.workstreamId)) ||
        (c.goalId && myGoalIds.has(c.goalId)),
    ),
    (c) => c.createdAt,
  ).slice(0, 8);

  const dailyUpdateSubmittedToday = snapshot.dailyUpdates.some(
    (u) => eq(u.engineer, alias) && u.date === today,
  );

  return {
    alias,
    activeStories,
    workstreams,
    goals,
    openBlockers,
    docsAwaitingReview,
    recentFollowedChanges,
    dailyUpdateSubmittedToday,
    today,
  };
}

// ---------------------------------------------------------------------------
// Team Vector (team home)
// ---------------------------------------------------------------------------

/** Goal status roll-up counts. */
export interface GoalStatusCounts {
  total: number;
  notStarted: number;
  onTrack: number;
  atRisk: number;
  offTrack: number;
  completed: number;
}

/** Counts goals by status. */
export function countGoalStatuses(goals: GoalSummary[]): GoalStatusCounts {
  const counts: GoalStatusCounts = {
    total: goals.length,
    notStarted: 0,
    onTrack: 0,
    atRisk: 0,
    offTrack: 0,
    completed: 0,
  };
  for (const g of goals) {
    switch (g.status) {
      case 'NOT_STARTED':
        counts.notStarted += 1;
        break;
      case 'ON_TRACK':
        counts.onTrack += 1;
        break;
      case 'AT_RISK':
        counts.atRisk += 1;
        break;
      case 'OFF_TRACK':
        counts.offTrack += 1;
        break;
      case 'COMPLETED':
        counts.completed += 1;
        break;
    }
  }
  return counts;
}

/** The shared team-home view-model. Managers and engineers see the same thing. */
export interface TeamVectorModel {
  /** BHAG-framework goal counts. */
  bhag: GoalStatusCounts;
  /** All-goal counts (fallback context when no BHAGs are tagged). */
  allGoals: GoalStatusCounts;
  /** Sprint/work delivery progress (stories done / total). */
  sprint: { total: number; done: number; pct: number };
  /** Workstreams not in a complete stage. */
  activeWorkstreams: WorkstreamSummary[];
  /** Open blockers across the team. */
  openBlockers: BlockerSummary[];
  /** How many of the open blockers depend on an external owner. */
  externalBlockerCount: number;
  /** Documents created within the reporting window. */
  newDocuments: TeamDocumentSummary[];
  /** Decisions logged within the reporting window. */
  decisionsThisWeek: DecisionSummary[];
  /** Notable changes within the reporting window (most recent first). */
  majorChanges: EngineeringChangeSummary[];
  /** Human-readable delivery-risk lines (blockers + off-track/at-risk goals). */
  deliveryRisks: string[];
  /** The look-back window used for "this week" sections, in days. */
  windowDays: number;
  today: string;
}

/**
 * Builds {@link TeamVectorModel} from a snapshot. `windowDays` controls the
 * "new this week" sections (default 7).
 */
export function buildTeamVector(
  snapshot: EntitySnapshot,
  now: Date = new Date(),
  windowDays = 7,
): TeamVectorModel {
  const today = todayLocalDate(now);

  const bhagGoals = snapshot.goals.filter(
    (g) => (g.goalFramework ?? '').trim().toUpperCase() === 'BHAG',
  );
  const bhag = countGoalStatuses(bhagGoals);
  const allGoals = countGoalStatuses(snapshot.goals);

  const doneStories = snapshot.tasks.filter(
    (t) => !t.deleted && t.status === 'DONE',
  ).length;
  const totalStories = snapshot.tasks.filter((t) => !t.deleted).length;
  const sprint = {
    total: totalStories,
    done: doneStories,
    pct: totalStories === 0 ? 0 : Math.round((doneStories / totalStories) * 100),
  };

  const activeWorkstreams = snapshot.workstreams.filter(
    (w) => !isCompleteStage(w.currentStage),
  );

  const openBlockers = snapshot.blockers.filter((b) => b.status === 'OPEN');
  const externalBlockerCount = openBlockers.filter(
    (b) => b.internalExternal === 'EXTERNAL',
  ).length;

  const newDocuments = byRecency(
    snapshot.documents.filter(
      (d) => withinDays(d.createdAt, windowDays, now) || withinDays(d.createdDate, windowDays, now),
    ),
    (d) => d.createdAt || d.createdDate,
  );

  const decisionsThisWeek = byRecency(
    snapshot.decisions.filter(
      (d) => withinDays(d.createdAt, windowDays, now) || withinDays(d.date, windowDays, now),
    ),
    (d) => d.createdAt || d.date,
  );

  const majorChanges = byRecency(
    snapshot.changes.filter((c) => withinDays(c.createdAt, windowDays, now)),
    (c) => c.createdAt,
  ).slice(0, 10);

  // --- Delivery risks (blocker impact + goal statuses) --------------------
  const deliveryRisks: string[] = [];
  const impact = selectOverallBlockerImpact(snapshot.blockers);
  const impactLine = describeBlockerImpact(impact);
  if (impactLine) deliveryRisks.push(impactLine);
  const offTrack = allGoals.offTrack;
  const atRisk = allGoals.atRisk;
  if (offTrack > 0) {
    deliveryRisks.push(
      `${offTrack} goal${offTrack === 1 ? '' : 's'} marked off track.`,
    );
  }
  if (atRisk > 0) {
    deliveryRisks.push(`${atRisk} goal${atRisk === 1 ? '' : 's'} at risk.`);
  }

  return {
    bhag,
    allGoals,
    sprint,
    activeWorkstreams,
    openBlockers,
    externalBlockerCount,
    newDocuments,
    decisionsThisWeek,
    majorChanges,
    deliveryRisks,
    windowDays,
    today,
  };
}
